/**
 * LoadsByChannelCard — Admin Reports breakdown of "Loaded In" totals split
 * by deposit CHANNEL (Cashier counter, Admin manual, Kiosk,
 * Self-serve Web, Admin Credit). Complements LoadsByMethodCard which groups
 * by payment instrument; this one groups by *who* recorded the load.
 *
 * Switchable period: today / week / month. Refreshes every 30s.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Layers, Loader2, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtMoney = (n) =>
    `$${Number(n || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const CHANNEL_TONE = {
    'Cashier Counter': 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
    'Admin Manual':    'border-amber-500/40 bg-amber-500/10 text-amber-200',
    'Legacy Auto-Deposit': 'border-sky-500/40 bg-sky-500/10 text-sky-200',
    'Kiosk':           'border-violet-500/40 bg-violet-500/10 text-violet-200',
    'Self-serve Web':  'border-blue-500/40 bg-blue-500/10 text-blue-200',
    'Admin Credit':    'border-pink-500/40 bg-pink-500/10 text-pink-200',
};

const PERIODS = [
    { key: 'today', label: 'Today' },
    { key: 'week',  label: 'This Week' },
    { key: 'month', label: 'This Month' },
];

const LoadsByChannelCard = ({ refreshKey = 0 }) => {
    const [period, setPeriod] = useState('today');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const { data: payload } = await axios.get(
                `${API}/admin/loads-by-channel-today?period=${period}`,
                { withCredentials: true }
            );
            setData(payload);
        } catch { /* silent — keep last good payload */ }
        finally { setLoading(false); }
    }, [period]);

    useEffect(() => {
        setLoading(true);
        fetchData();
        const id = setInterval(fetchData, 30_000);
        return () => clearInterval(id);
    }, [fetchData, refreshKey]);

    // Derive a sensible default date range for the export based on the
    // selected period. Bookkeeper can still override start/end via the prompt.
    const buildExportRange = () => {
        const today = new Date();
        const fmt = (d) => d.toISOString().slice(0, 10);
        if (period === 'today') {
            const s = fmt(today);
            return { start: s, end: s };
        }
        if (period === 'week') {
            // Last 7 days (rolling)
            const start = new Date(today);
            start.setDate(start.getDate() - 6);
            return { start: fmt(start), end: fmt(today) };
        }
        // month → first of current month → today
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: fmt(start), end: fmt(today) };
    };

    const downloadCsv = async () => {
        const { start, end } = buildExportRange();
        setExporting(true);
        try {
            const resp = await axios.get(
                `${API}/admin/loads-by-channel.csv?start_date=${start}&end_date=${end}`,
                { withCredentials: true, responseType: 'blob' }
            );
            const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `loads-by-channel_${start}_${end}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            // non-blocking — toast not imported here to keep deps light
            alert('Failed to export CSV');
        } finally { setExporting(false); }
    };

    return (
        <div className="card-dark p-4" data-testid="loads-by-channel-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-emerald-400" />
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                        Loaded In by Channel
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center rounded-md border border-white/10 overflow-hidden">
                        {PERIODS.map((p) => (
                            <button
                                key={p.key}
                                type="button"
                                onClick={() => setPeriod(p.key)}
                                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition ${
                                    period === p.key
                                        ? 'bg-emerald-500/20 text-emerald-200'
                                        : 'text-zinc-500 hover:text-zinc-300'
                                }`}
                                data-testid={`loads-by-channel-period-${p.key}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={downloadCsv}
                        disabled={exporting}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-500/20 disabled:opacity-50 transition"
                        title={`Download per-day breakdown by channel for the ${period === 'today' ? 'day' : period}.`}
                        data-testid="loads-by-channel-csv-btn"
                    >
                        {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                        CSV
                    </button>
                    <div className="text-right">
                        <p
                            className="text-xl font-mono font-bold text-white"
                            data-testid="loads-by-channel-grand-total"
                        >
                            {fmtMoney(data?.grand_total)}
                        </p>
                        <p className="text-[10px] text-zinc-500">
                            {data?.grand_count || 0} entries · CT
                        </p>
                    </div>
                </div>
            </div>

            {loading && !data ? (
                <div className="text-zinc-500 text-xs flex items-center gap-2 py-3">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading channel breakdown…
                </div>
            ) : !data?.channels?.length ? (
                <p className="text-xs text-zinc-500 text-center py-4">
                    No loads recorded yet for this period.
                </p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {data.channels.map((c) => {
                        const tone = CHANNEL_TONE[c.channel] || 'border-zinc-700 bg-zinc-900 text-zinc-200';
                        const pct = data.grand_total > 0 ? (c.total / data.grand_total) * 100 : 0;
                        return (
                            <div
                                key={c.transaction_type}
                                className={`rounded-md border ${tone} p-2.5`}
                                data-testid={`loads-by-channel-tile-${c.transaction_type}`}
                            >
                                <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">
                                    {c.channel}
                                </p>
                                <p className="text-lg font-mono font-bold mt-1">{fmtMoney(c.total)}</p>
                                <p className="text-[10px] opacity-70">
                                    {c.count} · {pct.toFixed(0)}%
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default LoadsByChannelCard;
